import React, { useState, useCallback } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';

const TrialBalance = () => {
  const [month, setMonth] = useState('1');
  const [category, setCategory] = useState('1');
  const [balanceType, setBalanceType] = useState('O');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const handleShow = useCallback(async () => {
    try {
      setLoading(true);
      setData(null);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        month: String(month),
        category: String(category),
        balanceType: String(balanceType)
      });
      const response = await fetch(
        `${baseURL}/accounting-entries/trial-balance?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load trial balance');
      }
      const result = await response.json();
      setData(result.data);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Error loading trial balance', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [month, category, balanceType]);

  const categoryLabel = { '1': 'General', 'C': 'Customers/Debtors', 'S': 'Creditors' };
  const balanceTypeLabel = { O: 'Opening (1st of month)', C: 'Closing (current date)', M: 'Month (end of month)' };

  return (
    <>
      <GirviPageShell
        title="Trial Balance"
        subtitle="Transactions · Statements"
        chipLabel="Transactions"
        maxWidth="xl"
      >
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Month"
                  type="number"
                  inputProps={{ min: 1, max: 12 }}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  helperText="01 - 12"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <MenuItem value="1">1 - General</MenuItem>
                    <MenuItem value="C">C - Customers/Debtors</MenuItem>
                    <MenuItem value="S">S - Creditors</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Balance Type</InputLabel>
                  <Select
                    value={balanceType}
                    label="Balance Type"
                    onChange={(e) => setBalanceType(e.target.value)}
                  >
                    <MenuItem value="O">O - Opening (1st of month)</MenuItem>
                    <MenuItem value="C">C - Closing (current date)</MenuItem>
                    <MenuItem value="M">M - Month (end of month)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleShow}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Show Trial Balance'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {data && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Trial Balance — {categoryLabel[data.category]} — {balanceTypeLabel[data.balanceType]} — As on {data.asOnDate}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CODE</TableCell>
                      <TableCell>NAME</TableCell>
                      <TableCell align="right">CLOSING DEBIT</TableCell>
                      <TableCell align="right">CLOSING CREDIT</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows && data.rows.length > 0 ? (
                      data.rows.map((row, idx) => (
                        <TableRow key={`${row.code}-${idx}`}>
                          <TableCell>{row.code}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right">
                            {row.closingDebit != null ? Number(row.closingDebit).toFixed(2) : ''}
                          </TableCell>
                          <TableCell align="right">
                            {row.closingCredit != null ? Number(row.closingCredit).toFixed(2) : ''}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No ledgers with balance for this selection
                        </TableCell>
                      </TableRow>
                    )}
                    {data.rows && data.rows.length > 0 && (
                      <TableRow sx={{ fontWeight: 'bold', backgroundColor: 'action.hover' }}>
                        <TableCell colSpan={2} align="right">Total</TableCell>
                        <TableCell align="right">{Number(data.totalClosingDebit || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{Number(data.totalClosingCredit || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {data.rows && data.rows.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Closing debit total and closing credit total {data.totalClosingDebit === data.totalClosingCredit ? 'match.' : 'do not match — check books.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </GirviPageShell>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TrialBalance;
